'use client';

import { CustomEventName, GTMConstants, GTMEventKey } from '@fe-cnyes/fe-common-gtm';
import LinkButton from '@fe-common-ui/LinkButton';
import Section from '@fe-common-ui/Section';
import { SCREEN_SIZE } from '@fe-common-utils/constants/screen';
import LinkFactory from '@fe-common-utils/libs/link';
import { fetchCnyesAd } from '@fe-news/api/ad';
import { OPEN_ACCOUNT_LINK } from '@fe-news/constants/links/buttons';
import useMediaQuery from '@fe-news/hooks/use-media-query';
import { styled } from '@linaria/react';
import { useQuery } from '@tanstack/react-query';

const TW_STOCK_HOMEPAGE = '台股首頁';
const SAVE_STOCK = '我要存股';
const GTM_SECTION_RELATED_PRODUCTS = `${GTMConstants.SECTION_PLACEHOLDER}_相關行情`;

const GTM_EVENT_CLICK_BUTTON_TW_STOCK_HOMEPAGE = {
  [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_BUTTON,
  [GTMEventKey.SECTION]: GTM_SECTION_RELATED_PRODUCTS,
  [GTMEventKey.CLICK_ITEM]: TW_STOCK_HOMEPAGE
};

const GTM_EVENT_CLICK_BUTTON_SAVE_STOCK = {
  [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_BUTTON,
  [GTMEventKey.SECTION]: GTM_SECTION_RELATED_PRODUCTS,
  [GTMEventKey.CLICK_ITEM]: SAVE_STOCK
};

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AdText = styled.a<{ marginBottom?: number }>`
  font-size: 14px;
  color: #e03f19;
  margin-left: 8px;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  text-decoration: none;
  font-weight: normal;
  flex: 1;
  margin-bottom: ${({ marginBottom }) => (marginBottom ? `${marginBottom}px` : 0)};

  &:before {
    content: '';
    position: relative;
    width: 18px;
    height: 11px;
    background: url('/assets/icons/arrows/icon-highlight.svg') no-repeat;
    display: inline-block;
    margin-right: 5px;
  }
`;

const Flex = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  justify-content: space-between;
`;

const OrderButtons = () => {
  return (
    <ButtonGroup>
      <LinkButton.Account
        href={`${LinkFactory.wwwChannelLink}/twstock`}
        gtmEvent={GTM_EVENT_CLICK_BUTTON_TW_STOCK_HOMEPAGE}
      >
        {TW_STOCK_HOMEPAGE}
      </LinkButton.Account>
      <LinkButton.Account href={OPEN_ACCOUNT_LINK.TWSTOCK} gtmEvent={GTM_EVENT_CLICK_BUTTON_SAVE_STOCK}>
        {SAVE_STOCK}
      </LinkButton.Account>
    </ButtonGroup>
  );
};

const DESKTOP_AD_ID = 'NewsEndQuote_D';
const MOBILE_AD_ID = 'NewsEndQuote_M';

type AdTextProps = {
  adText: { url: string; content: string };
};

const DesktopRelatedProductsSection = ({ adText }: AdTextProps) => {
  return (
    <Section
      title='相關行情'
      RightContent={
        <Flex>
          <AdText href={adText?.url || ''}>{adText?.content || ''}</AdText>
          <OrderButtons />
        </Flex>
      }
    />
  );
};

const MobileRelatedProductsSection = ({ adText }: AdTextProps) => {
  const haveAdText = adText && adText.content !== '';

  return (
    <>
      {haveAdText && (
        <AdText href={adText?.url || ''} marginBottom={16}>
          {adText?.content || ''}
        </AdText>
      )}
      <Section title='相關行情' RightContent={<OrderButtons />} />
    </>
  );
};

const RelatedProductsSection = () => {
  const isDesktop = useMediaQuery(`(min-width:${SCREEN_SIZE.DESKTOP}px)`);
  const adId = isDesktop ? DESKTOP_AD_ID : MOBILE_AD_ID;

  const { data: ad } = useQuery({
    queryKey: ['cnyes_ad', adId],
    queryFn: () => fetchCnyesAd(adId)
  });

  const adText = { url: ad?.url || '', content: ad?.content || '' };

  return isDesktop ? (
    <DesktopRelatedProductsSection adText={adText} />
  ) : (
    <MobileRelatedProductsSection adText={adText} />
  );
};

export default RelatedProductsSection;
